<script module lang="ts">
  import type { BaseProps } from "#lib/internal/types"
  import type { Snippet } from "svelte"

  // prettier-ignore
  export type AccordionTriggerProps = BaseProps<"button"> & {
    /** The controlled open state of the accordion item. */
    open?: boolean
    use?: any[]
    // svelte 5 snippets
    icon?: Snippet
    children?: Snippet
  };
</script>

<script lang="ts">
  import { Collapsible } from "#lib/internal/components/collapsible/index"
  import { useCollection } from "#lib/internal/helpers/collectionContext"
  import { cn } from "#lib/utils"
  import { ChevronDown } from "@tastyworks/icons"
  import { getItemContext } from "./AccordionItem.svelte"
  import { getRootCtx, getTriggerCollection } from "./AccordionRoot.svelte"

  type Props = AccordionTriggerProps

  let {
    class: className = undefined,
    open = $bindable(),
    use = [],
    icon,
    children,
    ...restProps
  }: Props = $props()

  let isOpen: boolean = $state(false)

  $effect(() => {
    open = isOpen
  })

  function createMutationObserver(node: HTMLElement) {
    isOpen = node.getAttribute("data-state") === "open"
    const observer = new MutationObserver((mutationList: MutationRecord[]) => {
      for (const mutation of mutationList) {
        if (mutation.attributeName === "data-state") {
          isOpen = node.getAttribute("data-state") === "open"
          return
        }
      }
    })

    observer.observe(node, {
      subtree: false,
      attributes: true,
      childList: false,
    })

    return {
      destroy() {
        observer.disconnect()
      },
    }
  }

  const itemCtx = getItemContext()
  const rootCtx = getRootCtx()
  const triggerCollection = getTriggerCollection()
  let triggerIndex = 0

  const handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault()
        e.stopPropagation()
        $triggerCollection[triggerIndex + 1]?.focus()
        break
      }
      case "ArrowUp": {
        e.preventDefault()
        e.stopPropagation()
        $triggerCollection[triggerIndex - 1]?.focus()
        break
      }
      case "Home": {
        e.preventDefault()
        e.stopPropagation()
        $triggerCollection[0]?.focus()
        break
      }
      case "End": {
        e.preventDefault()
        e.stopPropagation()
        $triggerCollection.at(-1)?.focus()
        break
      }
    }
  }

  function onIndexChange(index: number) {
    triggerIndex = index
  }

  function handleTriggerChange(value: boolean) {
    if ($rootCtx.type === "single") {
      $rootCtx.value = value ? $itemCtx.value : null
    } else if ($rootCtx.type === "multiple") {
      const prevValue = Array.isArray($rootCtx.value) ? $rootCtx.value : []
      const newValue = value
        ? [...prevValue, $itemCtx.value as typeof $itemCtx.value]
        : prevValue.filter((v) => v !== $itemCtx.value)
      $rootCtx.value = newValue as string[] | number[]
    }
  }
</script>

<Collapsible.Trigger
  use={[
    ...use,
    [
      useCollection,
      {
        collection: triggerCollection,
        onIndexChange,
      },
    ],
    [createMutationObserver],
  ]}
  onchange={handleTriggerChange}
  onkeydown={handleKeyDown}
  {...restProps as any}
  class={cn(
    "flex w-full flex-1 items-center justify-between py-4 font-medium transition-all",
    className
  )}
>
  {@render children?.()}
  <span class="transition-transform duration-200">
    {#if icon}
      {@render icon()}
    {:else}
      <ChevronDown class={isOpen ? "rotate-180" : ""} />
    {/if}
  </span>
</Collapsible.Trigger>
